import React from 'react';

import PropTypes from 'prop-types';
import { css } from 'aphrodite';
import { Modal } from 'react-bootstrap';
import {
  faExternalLinkAlt,
  faImages,
  faSearchPlus,
  faTimes,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { ProductType } from 'lib/enums/ProductType';
import { SIDE } from 'components/brand_sku_mapper_mfr_to_dp/constants';
import { countImages } from 'components/brand_sku_mapper_shared/imageHelpers';
import { galleryStyles } from 'components/brand_sku_mapper_shared/galleryStyles';
import { ProductImage } from 'components/brand_sku_mapper_shared/ProductImage';
import { styles } from 'components/brand_sku_mapper_mfr_to_dp/sharedStyles';
import { ZoomImageGallery } from 'components/brand_sku_mapper_shared/ZoomImageGallery';

// side: 'manufacturer' (the product being mapped) or 'vendor' (a candidate DP).
export function ProductPanel({ product, side, label, zoomOpen, onZoomOpenChange }) {
  if (!product) {
    return (
      <div className={css(styles.productCard)}>
        <div className={css(styles.sideLabel)}>{label}</div>
        <div className={css(styles.mutedText)}>No product.</div>
      </div>
    );
  }

  const imageCount = countImages(product.thumbnail_url, product.image_urls);

  const pdpHref =
    product.global_product_id && product.id
      ? `/global-product-detail/${product.global_product_id}/${product.id}/${
          side === SIDE.VENDOR ? ProductType.DISTRIBUTOR : ProductType.MANUFACTURER
        }`
      : null;

  const detailsLine =
    side === SIDE.VENDOR
      ? `${product.vendor || '—'} · SKU ${product.vendor_sku || '—'}`
      : `MFR SKU ${product.manufacturer_sku || '—'}`;
  const byLine =
    side === SIDE.VENDOR
      ? `${product.brand || '—'} · ${product.vendor || '—'}`
      : `${product.brand || '—'} by ${product.manufacturer || '—'}`;

  return (
    <div className={css(styles.productCard)}>
      <div className={css(styles.sideLabel)}>{label}</div>
      <div className={css(styles.productImageWrap)}>
        <div className={css(galleryStyles.productImageInner)}>
          <ProductImage
            src={product.thumbnail_url}
            imgClassName={css(styles.productImageLarge)}
            placeholderClassName={css(styles.productImagePlaceholderLarge)}
          />
          {imageCount > 0 && (
            <div className={css(galleryStyles.imageCountBadge)} title={`${imageCount} image(s)`}>
              <FontAwesomeIcon icon={faImages} /> {imageCount}
            </div>
          )}
        </div>
        <div className={css(styles.candIconRail, styles.productIconRail)}>
          <button
            type="button"
            className={css(styles.candIconButton)}
            onClick={() => onZoomOpenChange(true)}
            aria-label="Zoom in on product"
            title="Zoom in"
          >
            <FontAwesomeIcon icon={faSearchPlus} />
          </button>
          {pdpHref ? (
            <a
              href={pdpHref}
              target="_blank"
              rel="noopener noreferrer"
              className={css(styles.candIconButton)}
              aria-label="Open product page"
              title="Open product page"
            >
              <FontAwesomeIcon icon={faExternalLinkAlt} />
            </a>
          ) : (
            <span
              className={css(styles.candIconButton, styles.candIconButtonDisabled)}
              title="Product page unavailable"
              role="button"
              tabIndex={0}
              aria-disabled="true"
              aria-label="Product page unavailable"
            >
              <FontAwesomeIcon icon={faExternalLinkAlt} />
            </span>
          )}
        </div>
      </div>
      <div className={css(styles.productName)}>{product.name}</div>
      {product.data_source && (
        <span className={css(styles.dataSourceLabel)} title={product.data_source}>
          {product.data_source}
        </span>
      )}
      <div className={css(styles.mutedText)}>{byLine}</div>
      <div className={css(styles.mutedText)}>{product.pack_size || '—'}</div>
      <div className={css(styles.mutedText)}>{detailsLine}</div>
      {zoomOpen && (
        <Modal
          show
          onHide={() => onZoomOpenChange(false)}
          centered
          dialogClassName={css(styles.zoomModalDialog)}
        >
          <Modal.Body className={css(styles.zoomModalBody)}>
            <button
              type="button"
              className={css(styles.zoomCloseButton)}
              onClick={() => onZoomOpenChange(false)}
              aria-label="Close"
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
            <ZoomImageGallery thumbnailUrl={product.thumbnail_url} imageUrls={product.image_urls} />
            <div className={css(styles.zoomDetails)}>
              <div className={css(styles.zoomName)}>{product.name}</div>
              <div className={css(styles.mutedText)}>{byLine}</div>
              <div className={css(styles.mutedText)}>{product.pack_size || '—'}</div>
              <div className={css(styles.mutedText)}>{detailsLine}</div>
            </div>
          </Modal.Body>
        </Modal>
      )}
    </div>
  );
}

ProductPanel.propTypes = {
  product: PropTypes.shape({
    id: PropTypes.number,
    global_product_id: PropTypes.number,
    name: PropTypes.string,
    thumbnail_url: PropTypes.string,
    image_urls: PropTypes.arrayOf(PropTypes.string),
    vendor: PropTypes.string,
    brand: PropTypes.string,
    manufacturer: PropTypes.string,
    pack_size: PropTypes.string,
    vendor_sku: PropTypes.string,
    manufacturer_sku: PropTypes.string,
    data_source: PropTypes.string,
  }),
  side: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  zoomOpen: PropTypes.bool.isRequired,
  onZoomOpenChange: PropTypes.func.isRequired,
};
